import React, { useEffect, useState, useRef, useMemo } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import * as Popover from '@radix-ui/react-popover';
import { nanoid } from 'nanoid';
import Icon, { IconProps } from '../../DataDisplay/Icon/Icon';
import styles from './MultiSelect.module.scss';
import ErrorText from '../../Text/ErrorText/ErrorText';
import CircleButton from '../CircleButton/CircleButton';
import SelectOption, { type SelectOptionProps } from '../SelectOption/SelectOption';
import { withStyles } from '../../../utils/utils';
import { useDatadogLog } from '../logging';

/**
 * TODO:
 * - selectRef (for things like useFocusTrap, useClickOutside, etc)
 * - option grouping (<optgroup />)
 * - refactor any reusable things into their own modules
 */

export type MultiSelectFilterEvent<Data> = {
  filter: string;
  results: Data[];
  preventDefault: () => void;
  debounce: (cb: () => void, ms?: number) => void;
};

const cn = withStyles(styles);

export type DefaultOptionProps = {
  value?: string;
  label?: string;
} & object;

export type OptionContainerProps = Pick<SelectOptionProps, 'className' | 'children' | 'style'> & {
  onClick?: () => void;
};

export type SelectRenderOptionArgs = {
  Option: React.ComponentType<OptionContainerProps>;
  SelectedIcon: React.ComponentType<IconProps>;
  focused: boolean;
  selected: boolean;
  disabled: boolean;
  preventDefault: () => void;
  close: () => void;
};

export type MultiSelectProps<Data extends DefaultOptionProps = Record<string, never>> = {
  options: Data[];
  optionValue?: (o: Data) => string;
  optionLabel?: (o: Data) => string;
  disableOptionWhen?: (o: Data) => boolean;
  showClear?: boolean;
  placeholder?: React.ReactNode;
  name: string;
  value?: string[];
  errorMsg?: string;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  emptyFilterMsg?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<Element>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, args: { options: Data[] }) => void;
  chunk?: boolean;
  chunkSize?: number;
  readOnly?: boolean;
  renderDropdownHeader?: ({ close }: { close: () => void }) => React.ReactNode;
  renderDropdownFooter?: ({ close }: { close: () => void }) => React.ReactNode;
  renderOption?: (o: Data, args: SelectRenderOptionArgs) => React.ReactNode;
  style?: {
    '--triggerBorderRadius'?: string;
  };
  alignDropdown?: 'start' | 'center' | 'end';
  renderSelectedValue?: (
    o: Data[],
    args: {
      Defaults: React.ComponentType<DefaultSelectedOptionsContainerProps>;
      Placeholder: React.ComponentType<{ children?: React.ReactNode }>;
    },
  ) => React.ReactNode;
  filterable?: boolean;
  onFilter?: (e: MultiSelectFilterEvent<Data>) => void;
  sort?: boolean;
  sortOn?: (o: Data) => string | number;
  sortOrder?: 'asc' | 'desc';
};

function MultiSelect<Data extends DefaultOptionProps = Record<string, never>>({
  options,
  optionValue,
  optionLabel,
  placeholder = 'Select...',
  showClear = false,
  name,
  value = [],
  errorMsg,
  disabled: selectDisabled = false,
  loading = false,
  id,
  emptyFilterMsg = 'No results found',
  chunk = false,
  chunkSize = 20,
  onBlur = () => {},
  onChange = () => {},
  onFocus = () => {},
  readOnly = false,
  alignDropdown = 'start',
  renderDropdownHeader,
  renderDropdownFooter,
  renderOption,
  disableOptionWhen,
  renderSelectedValue,
  filterable = false,
  onFilter,
  sort = true,
  sortOn,
  sortOrder = 'asc',
}: MultiSelectProps<Data>) {
  const dropdownId = useRef(id || nanoid());
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const log = useDatadogLog();

  // We need currentValue so that we can watch it; we pass it into a hidden
  // input, and when it changes value, it will fire a native onChange event.
  // Hacky.
  const [curr, setCurr] = useState<string[]>([]);
  const selectedOptions = useMemo(() => options.filter((o) => curr.some((currO) => getValue(o) === currO)), [curr]);

  const [focusedOption, setFocusedOption] = useState<Data | null>(null);
  const [filtered, setFiltered] = useState(options);
  const limit = useMemo(() => (chunk ? chunkSize : filtered.length), [filtered, chunk, chunkSize]);
  const [offset, setOffset] = useState(1);
  const hasMore = useMemo(() => offset * limit < filtered.length - 1, [filtered, offset]);
  const filterRef = useRef<HTMLInputElement | null>(null);
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const finalOptions = useMemo(() => {
    const result = [...filtered].sort(sortFn).slice(0, offset * limit);

    return result;
  }, [filtered, offset, limit, sortOn, sort, sortOrder]);

  useEffect(() => {
    if (!focusedOption) return;
    if (!isOpen) return;

    const el = dropdownRef.current?.querySelector(`[id="${getOptionId(focusedOption)}"]`);

    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [focusedOption, finalOptions, isOpen]);

  useEffect(() => {
    setFiltered(options);
  }, [options]);

  useEffect(() => {
    if (!isOpen) return () => {};

    const filteredOptions = options.filter((o) => getLabelText(o).toLowerCase().includes(filter.toLowerCase()));

    let timeoutId: number;

    const debounce = (cb: () => void, ms = 1000) => {
      timeoutId = window.setTimeout(cb, ms);
    };

    if (onFilter) {
      let shouldOverrideFiltering = false;

      const preventDefault = () => {
        shouldOverrideFiltering = true;
      };

      onFilter({ filter, results: filteredOptions, preventDefault, debounce });

      if (!shouldOverrideFiltering) {
        setFiltered(filteredOptions);
      }
    } else {
      setFiltered(filteredOptions);
    }

    return () => clearTimeout(timeoutId);
  }, [filter]);

  useEffect(() => {
    const hasOption = finalOptions.some((fo) => getValue(fo) === getValue(focusedOption));

    if (!hasOption) {
      setFocusedOption(finalOptions[0]);
    }
  }, [finalOptions]);

  useEffect(() => {
    if (isOpen) {
      setOffset(1);
      setFilter('');
      filterRef.current?.focus();
      setFocusedOption(selectedOptions[0] || finalOptions[0]);
    } else {
      // set to null because keyboard event handlers keep firing after the dropdown is closed
      setFocusedOption(null);
    }
  }, [isOpen]);

  useEffect(() => {
    overrideValues(value);
  }, [value]);

  // call .blur() on select, and then the onBlur in the <select> will pick it up and emit the
  // React Synthetic event, as if we were using a regular <select> (hopefully)
  function handleBlur() {
    if (!hiddenInputRef.current) return;

    // blur only seems to work if we're already focused
    hiddenInputRef.current.focus();
    hiddenInputRef.current.blur();
  }

  function toggleMenu(opened: boolean) {
    if (!opened) {
      setIsOpen(false);

      return;
    }

    if (selectDisabled || readOnly) return;

    setFilter('');
    setIsOpen(true);
  }

  function isSameArray(a: string[], b: string[]) {
    if (a.length !== b.length) return false;

    return a.sort().toString() === b.sort().toString();
  }

  function overrideValues(newValues: string[]) {
    if (isSameArray(curr, newValues)) return;

    setCurr(newValues);
  }

  const handleChange = (newValue: string) => {
    const alreadySelected = curr.includes(newValue);
    const newValues = alreadySelected ? curr.filter((val) => val !== newValue) : [...curr, newValue];
    setCurr(newValues);
  };

  function fireChangeEvent(values: string[]) {
    if (!hiddenInputRef.current) return;

    // Get the native 'value' setter from the HTMLInputElement prototype
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      'value',
    )?.set?.bind(hiddenInputRef.current);

    if (nativeInputValueSetter) {
      // Call the native value setter with the new value
      nativeInputValueSetter.call(hiddenInputRef.current, values);

      const event = new Event('change', { bubbles: true });
      hiddenInputRef.current.dispatchEvent(event);
    }
  }

  useEffect(() => {
    fireChangeEvent(curr);
  }, [curr]);

  const handleKeyDownOpenDialog = (e: React.KeyboardEvent) => {
    const SKIP_AMOUNT = 5;

    if (!isOpen) {
      switch (e.key) {
        case 'Up':
        case 'ArrowUp':
        case 'Down':
        case 'ArrowDown':
          toggleMenu(true);
          return;
        case ' ': // Space
        case 'Enter':
          if (e.target !== toggleRef.current) return;
          e.preventDefault();
          toggleMenu(true);
          return;
        default:
          return;
      }
    }

    switch (e.key) {
      case 'Up':
      case 'ArrowUp': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx > 0) {
          setFocusedOption(finalOptions[idx - 1]);
        }

        return;
      }
      case 'Down':
      case 'ArrowDown': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx < finalOptions.length - 1) {
          setFocusedOption(finalOptions[idx + 1]);
        }

        return;
      }
      case 'Esc':
      case 'Escape':
        e.preventDefault();
        handleBlur();
        toggleMenu(false);
        toggleRef.current && toggleRef.current.focus();

        return;
      case 'PageUp': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx - SKIP_AMOUNT <= 0) {
          setFocusedOption(finalOptions[0]);
        } else {
          setFocusedOption(finalOptions[idx - SKIP_AMOUNT]);
        }

        return;
      }
      case 'PageDown': {
        e.preventDefault();
        const idx = finalOptions.findIndex(isOptionFocused);

        if (idx + SKIP_AMOUNT >= finalOptions.length - 1) {
          setFocusedOption(finalOptions[finalOptions.length - 1]);
        } else {
          setFocusedOption(finalOptions[idx + SKIP_AMOUNT]);
        }

        break;
      }
      default:
    }
  };

  function getValue(o: Data | null) {
    if (!o) {
      return '';
    }

    if (optionValue) return optionValue(o);

    return o.value || '';
  }

  function isOptionFocused(o: Data) {
    return getValue(o) === getValue(focusedOption);
  }

  function isOptionSelected(o: Data) {
    // return getValue(o) === curr;
    return curr.includes(getValue(o));
  }

  function isOptionDisabled(o: Data) {
    if (disableOptionWhen) {
      return disableOptionWhen(o);
    }

    return false;
  }

  function getLabelText(o: Data) {
    if (optionLabel) return optionLabel(o);

    return o.label || '';
  }

  const loadMore = () => {
    setOffset(offset + 1);
  };

  function getOptionId(o: Data | null) {
    if (!o) return '';

    return `${dropdownId.current}_option_${getValue(o)}`;
  }

  function sortFn(a: Data, b: Data) {
    if (!sort) return 0;

    const aVal = sortOn ? sortOn(a) : getLabelText(a);
    const bVal = sortOn ? sortOn(b) : getLabelText(b);

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    if (typeof aVal === 'string') {
      return sortOrder === 'asc' ? -1 : 1;
    }

    if (typeof bVal === 'string') {
      return sortOrder === 'asc' ? 1 : -1;
    }

    return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
  }

  const DefaultSelectedOptionsMemo = React.useMemo(
    () => (overrideProps: DefaultSelectedOptionsContainerProps) => (
      <DefaultSelectedOptions
        disabled={selectDisabled}
        selectedOptions={selectedOptions}
        getValue={(o) => getValue(o)}
        getLabelText={(o) => getLabelText(o)}
        onDelete={handleChange}
        {...overrideProps}
        style={{
          height: toggleRef.current?.clientHeight,
          ...overrideProps.style,
        }}
      />
    ),
    [curr, selectDisabled],
  );

  const PlaceholderMemo = React.useMemo(
    () => (overrideProps: { children?: React.ReactNode }) => (
      <Placeholder>{overrideProps.children || placeholder}</Placeholder>
    ),
    [curr],
  );

  return (
    <div
      role="none"
      className={cn('selectContainer')}
      onKeyDown={handleKeyDownOpenDialog}
      data-testid={`multiSelect-${id || name}`}
    >
      {/* Hidden input for native select behavior */}
      <input
        ref={hiddenInputRef}
        type="text"
        tabIndex={-1}
        id={id}
        name={name}
        onBlur={onBlur}
        onChange={(e) => {
          const selOpts = curr.map((val) => options.find((o) => getValue(o) === val)).filter((o): o is Data => !!o);
          onChange(e, { options: selOpts });
        }}
        className={cn('hiddenInputForFiringEvents', 'visuallyHidden')}
        aria-disabled={selectDisabled}
        aria-readonly={readOnly}
        aria-hidden
      />

      <Popover.Root open={isOpen}>
        <Popover.Trigger asChild>
          <div
            data-testid={`multiSelect-${id || name}-trigger`}
            ref={toggleRef}
            tabIndex={0}
            onKeyDown={() => {}}
            onClick={() => toggleMenu(!isOpen)}
            onFocus={(e: React.FocusEvent<Element>) => {
              onFocus(e);
            }}
            onBlur={() => {
              if (!isOpen) {
                handleBlur();
              }
            }}
            role="combobox"
            aria-disabled={selectDisabled}
            aria-readonly={readOnly}
            aria-haspopup="listbox"
            aria-controls={`${dropdownId.current}_dropdown`}
            aria-labelledby={`${dropdownId.current}_label`}
            aria-expanded={isOpen}
            aria-activedescendant={getOptionId(focusedOption)}
            className={cn('trigger', errorMsg && 'error')}
          >
            {renderSelectedValue ? (
              renderSelectedValue(selectedOptions, {
                // allow user to override the default onDelete.
                // Can add more overrides in DefaultSelectedOptionsContainerProps
                Defaults: DefaultSelectedOptionsMemo,
                Placeholder: PlaceholderMemo,
              })
            ) : selectedOptions.length > 0 ? (
              <DefaultSelectedOptionsMemo />
            ) : (
              <PlaceholderMemo />
            )}

            <div className={cn('triggerControls')}>
              {showClear && curr.length > 0 && (
                <CircleButton
                  icon="close"
                  circleFill={false}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    overrideValues([]);
                  }}
                />
              )}

              <Icon
                icon={loading ? 'loaderCropped' : 'angleDown'}
                height="14px"
                width="14px"
                className={cn('caret', selectDisabled && 'disabled')}
              />
            </div>
          </div>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            onInteractOutside={() => {
              handleBlur();
              toggleMenu(false);
            }}
            sideOffset={8}
            align={alignDropdown}
            ref={dropdownRef}
            className={cn('dropdown')}
            tabIndex={-1}
            aria-hidden={!isOpen}
            aria-disabled={selectDisabled}
            onWheel={(e) => {
              // allow dropdown scrolling in drawers/dialogs/etc
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // allow dropdown scrolling in drawers/dialogs/etc
              e.stopPropagation();
            }}
          >
            {filterable && (
              <div className={cn('filterContainer')}>
                <input
                  ref={filterRef}
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                  }}
                  placeholder="Search..."
                  readOnly={readOnly}
                  aria-label={`Search in ${name} options`}
                  aria-readonly={readOnly}
                />
              </div>
            )}

            {renderDropdownHeader &&
              renderDropdownHeader({
                close: () => {
                  handleBlur();
                  toggleMenu(false);
                },
              })}

            {finalOptions.length === 0 && <p className={cn('defaultOption')}>{emptyFilterMsg}</p>}

            <div className={cn('dropdownHeader')}>Select one or more options</div>
            <ul
              data-testid={`multiSelect-${id || name}-dropdown`}
              role="listbox"
              id={`${dropdownId.current}_dropdown`}
              aria-labelledby={`${dropdownId.current}_label`}
              tabIndex={-1}
              aria-multiselectable
              className={cn('options', 'scrollbar')}
            >
              {finalOptions.map((o) => {
                const selected = isOptionSelected(o);
                const focused = isOptionFocused(o);
                const val = getValue(o);
                const optionDisabled = isOptionDisabled(o);

                let defaultPrevented = false;

                const preventDefault = () => {
                  defaultPrevented = true;
                };

                if (renderOption) {
                  return (
                    <React.Fragment key={val}>
                      {renderOption(o, {
                        focused,
                        selected,
                        disabled: optionDisabled,
                        preventDefault,
                        close: () => {
                          handleBlur();
                          toggleMenu(false);
                        },
                        /**
                         * provide the container to the render prop with various
                         * props pre-configured (id, selected, focused, etc)
                         */
                        Option: React.memo<OptionContainerProps>((propsOverride) => (
                          <SelectOption
                            {...propsOverride}
                            id={getOptionId(o)}
                            selected={selected}
                            focused={focused}
                            disabled={optionDisabled}
                            className={cn('customOption', propsOverride.className)}
                            onMouseOver={() => {
                              setFocusedOption(o);
                            }}
                            onSelect={() => {
                              propsOverride.onClick && propsOverride.onClick();
                              log({
                                message: `${val} selected in ${name}`,
                                payload: { name, value: val },
                                actionType: 'Option',
                              });
                              if (!defaultPrevented) handleChange(val);
                            }}
                            onKeyDown={(e) => {
                              if (!isOpen) return;

                              const spaceIsForSelecting = e.key === ' ' && !filterable;

                              if (e.key === 'Enter' || spaceIsForSelecting) {
                                e.preventDefault();
                                propsOverride.onClick && propsOverride.onClick();

                                if (!defaultPrevented) handleChange(val);
                              }
                            }}
                          />
                        )),
                        SelectedIcon: React.memo((propsOverride) => (
                          <Icon icon="check" width="30px" height="15px" {...propsOverride} />
                        )),
                      })}
                    </React.Fragment>
                  );
                }

                return (
                  <SelectOption
                    key={val}
                    id={getOptionId(o)}
                    selected={selected}
                    focused={focused}
                    disabled={optionDisabled}
                    onMouseOver={() => {
                      setFocusedOption(o);
                    }}
                    onSelect={() => {
                      !optionDisabled && handleChange(val);
                      log({
                        message: `${val} selected in ${name}`,
                        payload: { name, value: val },
                        actionType: 'Option',
                      });
                    }}
                    onKeyDown={(e) => {
                      // We still want to prevent default, even if the option is disabled.
                      // Just don't fire the change event.
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        !optionDisabled && handleChange(val);
                      }

                      if (e.key === ' ' && !filterable) {
                        e.preventDefault();
                        !optionDisabled && handleChange(val);
                      }
                    }}
                  >
                    {getLabelText(o)}

                    {selected && <Icon icon="check" width="30px" height="15px" />}
                  </SelectOption>
                );
              })}
            </ul>

            {chunk && (
              <button type="button" disabled={!hasMore} onClick={loadMore} className={cn('loadMoreButton')}>
                <Icon icon="add" height="20px" width="20px" disabled={!hasMore} /> Load More
              </button>
            )}

            {renderDropdownFooter &&
              renderDropdownFooter({
                close: () => {
                  handleBlur();
                  toggleMenu(false);
                },
              })}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <ErrorText error={errorMsg} size="sm" fitContent />
    </div>
  );
}

export default MultiSelect;

type DefaultSelectedOptionsProps<Data extends DefaultOptionProps = Record<string, never>> = {
  disabled?: boolean;
  selectedOptions: Data[];
  getValue: (o: Data | null) => string;
  getLabelText: (o: Data) => string;
  onDelete: (val: string) => void;
  style?: React.CSSProperties;
};

// choose certain things to allow the parent to override, but make them optional
type DefaultSelectedOptionsContainerProps = Pick<Partial<DefaultSelectedOptionsProps>, 'onDelete' | 'style'>;

function DefaultSelectedOptions<Data extends DefaultOptionProps = Record<string, never>>({
  disabled = false,
  selectedOptions,
  getValue,
  getLabelText,
  onDelete,
  style,
}: DefaultSelectedOptionsProps<Data>) {
  return (
    <ScrollArea.Root className={cn('scrollAreaRoot')} style={style}>
      <ScrollArea.Viewport className={cn('scrollAreaViewport')}>
        <ul className={cn('selectedValues')}>
          {selectedOptions.map((o) => {
            const value = getValue(o);
            const label = getLabelText(o);

            return (
              <SelectedChip
                key={value}
                label={label}
                disabled={disabled}
                onDelete={() => {
                  onDelete(value);
                }}
              />
            );
          })}
        </ul>
      </ScrollArea.Viewport>

      <ScrollArea.Scrollbar className={cn('scrollAreaScrollbar')} orientation="horizontal">
        <ScrollArea.Thumb className={cn('scrollAreaThumb')} />
      </ScrollArea.Scrollbar>
    </ScrollArea.Root>
  );
}

function SelectedChip({
  disabled = false,
  label,
  onDelete,
}: {
  disabled?: boolean;
  label: string;
  onDelete: () => void;
}) {
  /**
   * Sigh... for some reason, if parent uses <Placeholder> in renderSelectedValue,
   * the user has to click the toggle TWICE to open it. So we have to hack it and
   * use mouseDown and mouseUp to track clicks...
   */
  const mouseDown = useRef(false);

  return (
    <li className={cn('selectedChip')}>
      {label}

      <CircleButton
        icon="close"
        size="small"
        circleFill={false}
        disabled={disabled}
        onMouseDown={(e) => {
          mouseDown.current = true;
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseUp={(e) => {
          if (mouseDown.current) {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }

          mouseDown.current = false;
        }}
        onKeyUp={(e) => {
          if (['Enter', ' '].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }
        }}
      />
    </li>
  );
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return <span className={cn('placeholder')}>{children}</span>;
}
